import React, { useState, useEffect, useRef } from "react";

import SelectField from "../../../genericInputFields/selectLabeled/selectLabeled.component";
import formStructure from "./constants/formStructure";
import Form from "./form";
import { contestTypeOptions } from "./constants/inputsConfig";
import { AccordionContent } from "./container.style";
import Section from "../../formComponents/section/section.component";
import useAppContext from "../../../../hooks/useAppContext";
import uniq from "lodash/uniq";

import { objectToForm } from "../../../../utils";
import useToast from "../../../../hooks/useToast";

import { ContainerStyledWrapper } from "./container.style";

const Container = ({ challenge }) => {
  const { axios } = useAppContext();
  const displayToast = useToast();
  const [contestType, setContestType] = useState(
    challenge?.contest_type || undefined,
  );
  const [serverErrors, setServerErrors] = useState(null);
  const [internalErrors, setInternalErrors] = useState(null);
  const [plainServerErrors, setPlainServerErrors] = useState(null);
  const errorSectionRef = useRef(null);

  const onTypeChange = (e) => setContestType(e);
  const onSubmit = (data) => {
    setPlainServerErrors(null);
    setServerErrors(null);
    setInternalErrors(null);
    const transformedData = {
      ...data,
    };
    if (data.reward_policy === "percentile_")
      transformedData["reward_policy"] =
        `${data.reward_policy}${data.percentile_number}`;
    const contestPermissions = data.contest_permissions?.map((cp) => {
      const tmp = { ...cp };
      if (Array.isArray(cp.criteria)) {
        tmp.criteria = cp.criteria.join(", ");
      }
      return tmp;
    });
    transformedData["contest_permissions"] = [...contestPermissions];
    transformedData["target_problem_solvers"] = {
      category: "tier",
      permission_type: "inclusion",
      criteria: data.target_problem_solvers.criteria,
    };

    document.body.style.cursor = "wait";
    axios
      .post("admin/contests", objectToForm(transformedData), {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((resp) => {
        const { redirect_to } = resp.data.data;
        const finishedCallback = () => {
          if (window) window.location.assign(redirect_to);
        };
        displayToast({
          success: true,
          message: "Request successfull",
          finishedCallback: finishedCallback,
        });
      })
      .catch((resp) => {
        if (resp.status === 500) {
          displayToast({ success: false, message: "Something went wrong..." });
        } else {
          const respServerErrors = resp.response.data.data.errors;
          typeof respServerErrors === "string"
            ? setPlainServerErrors(respServerErrors)
            : setServerErrors({ ...respServerErrors });
          displayToast({
            success: false,
            message: "Validation didn't pass. Correct the form",
          });
        }
      })
      .finally(() => {
        document.body.style.cursor = "default";
      });
  };

  useEffect(() => {
    if (!plainServerErrors) return;
    errorSectionRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [plainServerErrors]);

  useEffect(() => {
    if (!serverErrors) return;
    errorSectionRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [serverErrors]);

  const onError = (errors) => {
    setPlainServerErrors(null);
    setServerErrors(null);
    setInternalErrors(errors);
    displayToast({ success: false, message: "Form is not valid." });
  };

  // Some client-side only libs have to be preloaded before the form appears,
  // so the layout does not flicker that much
  useEffect(() => {
    import("@mbo/form-advanced");
    import(
      "../../../genericInputFields/jsonEditorReact/jsonEditorReact.component"
    );
  }, []);

  const displayServerErrors =
    (serverErrors || internalErrors) &&
    uniq(
      Object.entries({ ...serverErrors, ...internalErrors }).map(([key]) => {
        const lookup = formStructure[contestType].items;
        const accordion = formStructure[contestType].accordion;

        const getFieldDisplayName = () => {
          if (accordion) {
            for (const [pKey, items] of Object.entries(lookup)) {
              const match = items.find((itm) => {
                if (Array.isArray(itm.fields))
                  return itm.fields.some((obj) => obj.name === key);
                else return itm.fields.name === key;
              });
              if (match) return `${pKey} > ${match.name}`;
            }
          } else {
            const match = lookup.find((itm) => {
              if (Array.isArray(itm.fields))
                return itm.fields.some((obj) => obj.name === key);
              else return itm.fields.name === key;
            });
            if (match) return match.name;
          }
          return key;
        };

        return getFieldDisplayName();
      }),
    );

  return (
    <ContainerStyledWrapper>
      <AccordionContent $dark>
        <Section
          data={{
            name: "Select a challenge type",
            description:
              "Select the type of challenge you want to create, based on the format/goals of your campaign.",
            required: true,
          }}
        >
          <SelectField
            initialValue={challenge?.contest_type || undefined}
            useInForm={false}
            onChange={onTypeChange}
            name="contest_type"
            options={contestTypeOptions}
            readOnly={!!challenge?.contest_type}
          />
        </Section>
      </AccordionContent>
      {(serverErrors || internalErrors || plainServerErrors) && (
        <AccordionContent $error ref={errorSectionRef}>
          <h6>Validation errors</h6>
          {(serverErrors || internalErrors) && (
            <>
              <p>Please chceck and correct the following sections:</p>
              <ul>
                {displayServerErrors.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </>
          )}
          {plainServerErrors && <div>{plainServerErrors}</div>}
        </AccordionContent>
      )}
      {contestType && (
        <Form
          onPostForm={onSubmit}
          onPostFormError={onError}
          structure={formStructure[contestType]}
          contestType={contestType}
          challenge={challenge}
          serverErrors={serverErrors}
        />
      )}
    </ContainerStyledWrapper>
  );
};

export default Container;
